import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Typography,
  Paper,
  Button,
  Box,
  TextField,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { ROUTES } from "../../constants";

const PendingWellRegistrationsList = ({ registrations }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const filteredRegistrations = useMemo(() => {
    if (!searchTerm) return registrations;
    const term = searchTerm.toLowerCase();
    return registrations.filter((reg) => {
      return (
        `${reg.pending_well_reg_ndx || ""}`.toLowerCase().includes(term) ||
        `${reg.name_first_owner || ""} ${reg.name_last_owner || ""}`
          .toLowerCase()
          .includes(term) ||
        `${reg.name_company_owner || ""}`.toLowerCase().includes(term) ||
        `${reg.address_city_location || ""}`.toLowerCase().includes(term) ||
        `${reg.address_line_1_location || ""}`.toLowerCase().includes(term) ||
        `${reg.email_owner || ""}`.toLowerCase().includes(term)
      );
    });
  }, [registrations, searchTerm]);

  return (
    <Box sx={{ width: "100%", px: 2, py: 3 }}>
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{
          maxWidth: "1000px",
          margin: "0 auto",
          borderBottom: "1px solid rgba(0, 0, 0, 0.5)",
          pb: 4,
          mb: 4,
        }}
      >
        <Grid item xs={12} sm={6}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                border: "2px solid",
                borderColor: "secondary.main",
                borderRadius: 2,
                px: 2,
                py: 1,
                minWidth: 64,
                textAlign: "center",
              }}
            >
              <Typography variant="h4" color="secondary" fontWeight={700}>
                {registrations.length}
              </Typography>
            </Box>
            <Typography variant="h6">Pending Well Registrations</Typography>
          </Box>
        </Grid>

        <Grid item xs>
          <TextField
            placeholder="Search by id, contact info, or proposed well location"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Grid>
      </Grid>

      {(!filteredRegistrations || filteredRegistrations.length === 0) && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mt: 4 }}
        >
          No pending registrations found.
        </Typography>
      )}

      <Grid container spacing={3} justifyContent="center">
        {registrations.map((reg) => {
          const editUrl = `${ROUTES.PENDING_WELL_REGISTRATIONS_MANAGEMENT}/${reg.pending_well_reg_ndx}`;
          return (
            <Grid item xs={12} md={10} lg={8} key={reg.pending_well_reg_ndx}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    #{reg.pending_well_reg_ndx}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {[reg.name_first_owner, reg.name_last_owner]
                      .filter(Boolean)
                      .join(" ") || "—"}
                  </Typography>
                  {reg.name_company_owner && (
                    <Typography variant="body2" color="text.secondary">
                      {reg.name_company_owner}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Owner Contact Info:
                  </Typography>
                  {reg.email_owner && (
                    <Typography variant="body2" color="text.secondary">
                      Email: {reg.email_owner}
                    </Typography>
                  )}
                  {reg.phone_primary_owner && (
                    <Typography variant="body2" color="text.secondary">
                      Phone: {reg.phone_primary_owner}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Proposed Well Address:
                  </Typography>
                  {reg.address_line_1_location && (
                    <Typography variant="body2" color="text.secondary">
                      {reg.address_line_1_location}
                    </Typography>
                  )}
                  {(reg.address_city_location ||
                    reg.address_state_location) && (
                    <Typography variant="body2" color="text.secondary">
                      {[reg.address_city_location, reg.address_state_location]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 1 }} />

                {reg.dt_received && (
                  <Typography variant="body2" color="text.secondary">
                    Submitted: {reg.dt_received.substring(0, 10)}
                  </Typography>
                )}

                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  {isXs ? (
                    <IconButton component={Link} to={editUrl} color="secondary">
                      <EditIcon />
                    </IconButton>
                  ) : (
                    <Button
                      component={Link}
                      to={editUrl}
                      variant="outlined"
                      color="secondary"
                      startIcon={<EditIcon />}
                    >
                      Review
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default PendingWellRegistrationsList;
